/**
 * ImportPolesDialog — import stĺpov zo súboru.
 *
 * Prepísané 2026-09-30. Predtým dialóg len rozparsoval súbor a MapView potom
 * vytváral stĺpy PO JEDNOM vo slučke. Keď niektorý spadol, slučka sa prerušila,
 * prvé stĺpy už v projekte boli, hláška povedala „Import failed. Please try
 * again." — a opakovanie importu ich vytvorilo druhýkrát.
 *
 * Teraz ide celý súbor jedným volaním na server, ktorý:
 *   - preskočí stĺpy s názvom, ktorý v projekte už je (takže opakovaný import
 *     nič nezdvojí),
 *   - pri chybnom riadku neuloží NIČ a povie, ktorý riadok to bol,
 *   - vráti rozpis vytvorených, preskočených a chybných.
 */
import React, { useState, useRef } from 'react';
import { importPolesFromFile, ImportedPole } from '../../utils/csvImporter';
import { polesAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';

type PoleType = 'pole' | 'manhole' | 'utility_pole';

interface VysledokImportu {
  created: number;
  skipped: { riadok: number; nazov: string; dovod: string }[];
  errors: { riadok: number; nazov: string; chyba: string }[];
  poznamka?: string | null;
}

interface ImportPolesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  /** Zavolá sa po importe — mapa si má znovu natiahnuť stĺpy */
  onImported?: () => void;
}

export const ImportPolesDialog: React.FC<ImportPolesDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  onImported,
}) => {
  const [nacitavam, setNacitavam] = useState(false);
  const [importujem, setImportujem] = useState(false);
  const [preview, setPreview] = useState<ImportedPole[]>([]);
  const [format, setFormat] = useState<string>('');
  const [chybySuboru, setChybySuboru] = useState<string[]>([]);
  const [vysledok, setVysledok] = useState<VysledokImportu | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);

  const [typ, setTyp] = useState<PoleType>('pole');
  const [odvodiZNazvu, setOdvodiZNazvu] = useState(true);
  const [preskocitDuplikaty, setPreskocitDuplikaty] = useState(true);
  const [prehodit, setPrehodit] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const suborRef = useRef<File | null>(null);

  const precitaj = async (file: File, swap: boolean) => {
    setNacitavam(true);
    setChybySuboru([]);
    setPreview([]);
    setVysledok(null);
    setChyba(null);
    try {
      const r = await importPolesFromFile(file, swap);
      setPreview(r.poles);
      setFormat(r.format || '');
      setChybySuboru(r.errors);
      if (r.poles.length === 0 && r.errors.length === 0) {
        setChybySuboru(['V súbore som nenašiel žiadne stĺpy.']);
      }
    } catch (e: any) {
      setChybySuboru([`Súbor sa nepodarilo prečítať: ${e?.message || e}`]);
    } finally {
      setNacitavam(false);
    }
  };

  const vyberSuboru = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    suborRef.current = file;
    await precitaj(file, prehodit);
  };

  const prepniPrehodenie = async (hodnota: boolean) => {
    setPrehodit(hodnota);
    if (suborRef.current) await precitaj(suborRef.current, hodnota);
  };

  const importuj = async () => {
    if (preview.length === 0) return;
    setImportujem(true);
    setChyba(null);
    try {
      const r: any = await polesAPI.importPoles(projectId, {
        poles: preview.map((p) => ({
          name: p.name,
          latitude: p.latitude,
          longitude: p.longitude,
          description: p.description,
        })),
        default_pole_type: typ,
        derive_type_from_name: odvodiZNazvu,
        skip_duplicates: preskocitDuplikaty,
      });
      setVysledok(r.data);
      if (r.data?.created > 0 && onImported) onImported();
    } catch (e: any) {
      setChyba(getErrorMessage(e, 'Import zlyhal.'));
    } finally {
      setImportujem(false);
    }
  };

  const zavri = () => {
    setPreview([]);
    setChybySuboru([]);
    setVysledok(null);
    setChyba(null);
    setFormat('');
    suborRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={zavri}>
      <div className="dialog" style={{ maxWidth: '760px' }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📥 Import stĺpov</h2>
          <button className="dialog-close" onClick={zavri}>✕</button>
        </div>

        <div className="dialog-content">
          {chyba && <div className="dialog-error">{chyba}</div>}

          {/* ── Výsledok importu ── */}
          {vysledok ? (
            <div>
              {vysledok.poznamka ? (
                <div className="dialog-error">⚠️ {vysledok.poznamka}</div>
              ) : (
                <div style={{
                  background: '#14532d', color: '#86efac', borderRadius: '6px',
                  padding: '10px 14px', marginBottom: '14px', fontSize: '14px',
                }}>
                  ✅ Vytvorených stĺpov: <b>{vysledok.created}</b>
                </div>
              )}

              {vysledok.skipped.length > 0 && (
                <div className="form-group">
                  <label>Preskočené ({vysledok.skipped.length})</label>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '12px' }}>
                    {vysledok.skipped.map((s, i) => (
                      <div key={i} style={{ color: '#e0a800', padding: '2px 0' }}>
                        riadok {s.riadok}: <b>{s.nazov}</b> — {s.dovod}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {vysledok.errors.length > 0 && (
                <div className="form-group">
                  <label>Chybné ({vysledok.errors.length})</label>
                  <div style={{ maxHeight: '140px', overflowY: 'auto', fontSize: '12px' }}>
                    {vysledok.errors.map((s, i) => (
                      <div key={i} style={{ color: '#f87171', padding: '2px 0' }}>
                        riadok {s.riadok}: <b>{s.nazov}</b> — {s.chyba}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── Voľba súboru ── */}
              <div className="form-group">
                <label>Súbor</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.kml"
                  onChange={vyberSuboru}
                  disabled={nacitavam || importujem}
                />
                <small className="form-hint">
                  CSV alebo TXT (export z Google MyMaps s <code>POINT (dĺžka šírka)</code>,
                  alebo stĺpce so šírkou a dĺžkou), prípadne KML. Oddeľovač čiarka,
                  stredník aj tabulátor. KMZ treba najprv rozbaliť a nahrať .kml zvnútra.
                </small>
              </div>

              {nacitavam && <p style={{ color: '#999' }}>Čítam súbor…</p>}

              {chybySuboru.length > 0 && (
                <div className="form-group">
                  <label>Súbor — problémy ({chybySuboru.length})</label>
                  <div style={{ maxHeight: '140px', overflowY: 'auto', fontSize: '12px' }}>
                    {chybySuboru.map((e, i) => (
                      <div key={i} style={{ color: '#e0a800', padding: '2px 0' }}>{e}</div>
                    ))}
                  </div>
                </div>
              )}

              {preview.length > 0 && (
                <>
                  <div className="form-group">
                    <label>
                      Načítané zo súboru: {preview.length}{' '}
                      <span style={{ color: '#999', fontWeight: 'normal' }}>
                        ({format})
                      </span>
                    </label>
                    <div style={{
                      maxHeight: '200px', overflowY: 'auto', fontSize: '12px',
                      border: '1px solid #333', borderRadius: '6px', padding: '6px',
                    }}>
                      {preview.slice(0, 100).map((p, i) => (
                        <div key={i} style={{ display: 'flex', gap: '10px', padding: '2px 0' }}>
                          <span style={{ color: '#999', width: '28px' }}>{i + 1}.</span>
                          <span style={{ flex: 1, minWidth: 0 }}>{p.name}</span>
                          <span style={{ color: '#999', fontFamily: 'monospace' }}>
                            {p.latitude.toFixed(6)}, {p.longitude.toFixed(6)}
                          </span>
                        </div>
                      ))}
                      {preview.length > 100 && (
                        <div style={{ color: '#999', paddingTop: '4px' }}>
                          … a ďalších {preview.length - 100}
                        </div>
                      )}
                    </div>
                    <small className="form-hint">
                      Skontroluj súradnice — na Slovensku je šírka okolo 48–49
                      a dĺžka okolo 17–22. Ak sú naopak, prehoď ich nižšie.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Typ stĺpov</label>
                    <select value={typ} onChange={(e) => setTyp(e.target.value as PoleType)}>
                      <option value="pole">Stĺpik (~1 m, skrinka pre zemnú inštaláciu)</option>
                      <option value="manhole">🕳️ Šachta</option>
                      <option value="utility_pole">Stĺp (elektrikársky, vzdušné vedenie)</option>
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={odvodiZNazvu}
                        onChange={(e) => setOdvodiZNazvu(e.target.checked)}
                      />
                      <span>Odvodiť typ z názvu</span>
                    </label>
                    <small className="form-hint">
                      Názov so „Šachta" dá šachtu, so „Stĺpik" stĺpik, so „Stĺp" stĺp.
                      Čo sa neodvodí, dostane typ zvolený vyššie.
                    </small>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={preskocitDuplikaty}
                        onChange={(e) => setPreskocitDuplikaty(e.target.checked)}
                      />
                      <span>Preskočiť stĺpy, ktorých názov v projekte už je</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={prehodit}
                        onChange={(e) => prepniPrehodenie(e.target.checked)}
                      />
                      <span>Prehodiť zemepisnú šírku a dĺžku</span>
                    </label>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="dialog-actions">
          <button className="btn-secondary" onClick={zavri}>
            {vysledok ? 'Zavrieť' : 'Zrušiť'}
          </button>
          {!vysledok && (
            <button
              className="btn-primary"
              disabled={preview.length === 0 || importujem || nacitavam}
              onClick={importuj}
            >
              {importujem ? 'Importujem…' : `📥 Importovať ${preview.length} stĺpov`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
